import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';

import { cn } from '@/lib/cn';

/**
 * Panel — a full-height region of app chrome: the detail pane, a filter rail, a column
 * of a split view.
 *
 * Unlike Card it does not float. It fills its slot, owns a fixed-height header on the
 * `--h-topbar` metric so it lines up with the shell, and scrolls only in its body.
 */

export type PanelEdge = 'none' | 'left' | 'right' | 'top' | 'bottom' | 'all';

const EDGE: Record<PanelEdge, string> = {
  none: '',
  left: 'border-l border-line',
  right: 'border-r border-line',
  top: 'border-t border-line',
  bottom: 'border-b border-line',
  all: 'border border-line',
};

export interface PanelProps extends Omit<ComponentPropsWithoutRef<'section'>, 'title'> {
  title?: ReactNode;
  /** Small muted line under the title. Keeps the header on `--h-topbar` by clamping. */
  subtitle?: ReactNode;
  /** Leading header slot — a back button, an avatar. */
  leading?: ReactNode;
  /** Trailing header slot — icon buttons, an overflow menu. */
  actions?: ReactNode;
  /** Row directly below the header: tabs, a filter bar. Not scrolled with the body. */
  toolbar?: ReactNode;
  footer?: ReactNode;
  edge?: PanelEdge;
  /** Removes the body padding for panels that host a table or their own layout. */
  bare?: boolean;
  /** Turns off the body scroll container (the child owns scrolling). */
  scroll?: boolean;
  tone?: 'surface' | 'canvas';
  children?: ReactNode;
  /** Accessible name when there is no visible title. */
  label?: string;
}

export const Panel = forwardRef<HTMLElement, PanelProps>(function Panel(
  {
    title,
    subtitle,
    leading,
    actions,
    toolbar,
    footer,
    edge = 'none',
    bare = false,
    scroll = true,
    tone = 'surface',
    className,
    children,
    label,
    ...rest
  },
  ref,
) {
  const hasHeader = Boolean(title || leading || actions || subtitle);

  return (
    <section
      ref={ref}
      aria-label={label ?? (typeof title === 'string' ? title : undefined)}
      className={cn(
        'flex min-h-0 min-w-0 flex-col',
        tone === 'surface' ? 'bg-[var(--surface-1)]' : 'bg-[var(--canvas)]',
        EDGE[edge],
        className,
      )}
      {...rest}
    >
      {hasHeader ? (
        <header className="flex h-[var(--h-topbar)] shrink-0 items-center gap-3 border-b border-line px-4">
          {leading ? <div className="flex shrink-0 items-center gap-2">{leading}</div> : null}
          <div className="min-w-0 flex-1">
            {title ? (
              <h2 className="truncate text-md font-semibold tracking-[var(--tracking-tight)] text-primary">
                {title}
              </h2>
            ) : null}
            {subtitle ? (
              <p className="truncate text-sm leading-snug text-muted">{subtitle}</p>
            ) : null}
          </div>
          {actions ? <div className="flex shrink-0 items-center gap-1">{actions}</div> : null}
        </header>
      ) : null}

      {toolbar ? (
        <div className="flex shrink-0 items-center gap-2 border-b border-line-subtle px-4 py-2">
          {toolbar}
        </div>
      ) : null}

      <div
        className={cn(
          'min-h-0 min-w-0 flex-1',
          scroll && 'overflow-y-auto overscroll-contain',
          !bare && 'p-4',
        )}
      >
        {children}
      </div>

      {footer ? (
        <div className="flex shrink-0 items-center gap-2 border-t border-line bg-[var(--surface-2)] px-4 py-3">
          {footer}
        </div>
      ) : null}
    </section>
  );
});

/** Titled block inside a panel body — the detail pane's repeated unit. */
export interface PanelSectionProps extends Omit<ComponentPropsWithoutRef<'div'>, 'title'> {
  title?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}

export const PanelSection = forwardRef<HTMLDivElement, PanelSectionProps>(function PanelSection(
  { title, actions, className, children, ...rest },
  ref,
) {
  return (
    <div ref={ref} className={cn('min-w-0', className)} {...rest}>
      {title || actions ? (
        <div className="mb-2 flex h-[var(--h-control-sm)] items-center gap-2">
          {title ? <h3 className="eyebrow truncate">{title}</h3> : null}
          {actions ? <div className="ml-auto flex shrink-0 items-center gap-1">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </div>
  );
});
